import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  Space,
  Spin,
  Table,
  Tabs,
  Typography,
  message,
} from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import axios from 'axios';
import { useSalesByRepresentatives } from '../../Hooks/useSalesByRepresentatives';
import Api from '../../Network';
import { usePermissions } from '../../contexts/PermissionContext';
import SalesRepPaymentPercentage from '../../Component/SalesRepPaymentPercentage';
import {
  totalPaymentPercentage,
  hasInvoicesInPeriod,
} from '../../utils/salesRepresentativePayment';
import { REPORT_PERMISSION_CONTEXT } from '../../constants/reportPermissionContext';
import { SALES_REPORT_PERMISSIONS } from '../../constants/reportPermissions';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

interface RepRow {
  sales_representative_id: number;
  sales_representative_name: string;
}

interface RepDetailRow {
  customer_id: number;
  customer_name: string;
  total_invoices_amount: number;
  total_paid_amount: number;
  total_due_amount: number;
  payment_percentage: number | null;
  has_invoices_in_period?: boolean;
  total_receipts_amount: number;
}

const SalesReport: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { canAccessMenuRoute } = usePermissions();
  const canViewReport = canAccessMenuRoute('/reports/sales', [SALES_REPORT_PERMISSIONS.VIEW]);
  const canDownloadReport = canAccessMenuRoute('/reports/sales', [
    SALES_REPORT_PERMISSIONS.DOWNLOAD,
  ]);
  const canLoadRepList = canViewReport || canDownloadReport;
  const [rangeDraft, setRangeDraft] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [rangeApplied, setRangeApplied] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);

  const startDate = useMemo(
    () => (rangeApplied[0] ? rangeApplied[0].format('YYYY-MM-DD') : undefined),
    [rangeApplied]
  );
  const endDate = useMemo(
    () => (rangeApplied[1] ? rangeApplied[1].format('YYYY-MM-DD') : undefined),
    [rangeApplied]
  );

  const { data: salesByRepData, loading: salesLoading } = useSalesByRepresentatives(
    startDate,
    endDate,
    canLoadRepList,
    REPORT_PERMISSION_CONTEXT.SALES_REPORT
  );

  const reps: RepRow[] = (salesByRepData?.sales_by_representatives || []) as RepRow[];
  const [selectedRepresentatives, setSelectedRepresentatives] = useState<number[]>([]);
  const [activeTab, setActiveTab] = useState<string>('');
  const [representativeDetails, setRepresentativeDetails] = useState<{
    [key: number]: RepDetailRow[];
  }>({});
  const [loadingDetails, setLoadingDetails] = useState<{ [key: number]: boolean }>({});
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    const allIds = reps.map((r) => r.sales_representative_id);
    setSelectedRepresentatives(allIds);
    setRepresentativeDetails({});
    if (allIds.length > 0) {
      setActiveTab(String(allIds[0]));
    } else {
      setActiveTab('');
    }
  }, [startDate, endDate, reps.length]);

  const loadRepresentativeDetails = useCallback(
    async (representativeId: number) => {
      if (representativeDetails[representativeId]) {
        return;
      }
      setLoadingDetails((prev) => ({ ...prev, [representativeId]: true }));
      try {
        const response: any = await Api.get('/api/admin/dashboard/sales-representative-details', {
          params: {
            representative_id: representativeId,
            start_date: startDate,
            end_date: endDate,
            permission_context: REPORT_PERMISSION_CONTEXT.SALES_REPORT,
          },
        });
        if (response?.status && response?.data) {
          setRepresentativeDetails((prev) => ({
            ...prev,
            [representativeId]: response.data as RepDetailRow[],
          }));
        }
      } catch (error: any) {
        message.error(error?.response?.data?.message || t('common.error'));
      } finally {
        setLoadingDetails((prev) => ({ ...prev, [representativeId]: false }));
      }
    },
    [endDate, representativeDetails, startDate, t]
  );

  useEffect(() => {
    const repId = Number(activeTab);
    if (repId > 0 && canViewReport) {
      loadRepresentativeDetails(repId);
    }
  }, [activeTab, canViewReport, loadRepresentativeDetails]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRepresentatives(reps.map((r) => r.sales_representative_id));
    } else {
      setSelectedRepresentatives([]);
    }
  };

  const handleSelectRepresentative = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedRepresentatives([...selectedRepresentatives, id]);
    } else {
      setSelectedRepresentatives(selectedRepresentatives.filter((repId) => repId !== id));
    }
  };

  const renderAmount = (value: number) =>
    `${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0)} ${i18n.language === 'en' ? 'SAR' : 'ر.س'}`;

  const handleExportReport = async () => {
    if (!canDownloadReport) {
      message.error(
        t(
          'salesRepresentatives.salesAnalytics.downloadForbidden',
          'You do not have permission to download this report.'
        )
      );
      return;
    }
    if (!startDate || !endDate) {
      message.warning(t('dashboard.selectDateRange'));
      return;
    }

    if (selectedRepresentatives.length === 0) {
      message.warning('Please select at least one representative');
      return;
    }

    setExportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const baseURL = process.env.REACT_APP_API_URL || 'https://driveshield.net';
      const apiBaseURL = baseURL.startsWith('http') ? baseURL : `http://${baseURL}`;

      const response = await axios.get(
        `${apiBaseURL}/api/admin/dashboard/export-sales-by-representatives`,
        {
          params: {
            start_date: startDate,
            end_date: endDate,
            representative_ids: selectedRepresentatives.join(','),
            permission_context: REPORT_PERMISSION_CONTEXT.SALES_REPORT,
          },
          responseType: 'blob',
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
        }
      );

      if (!(response.data instanceof Blob)) {
        throw new TypeError('Invalid response format from export API');
      }

      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.message || errorData.error || 'Export failed');
      }

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sales_by_representatives_${startDate}_to_${endDate}.xlsx`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(url);

      message.success(t('dashboard.exportSuccess'));
    } catch (error: any) {
      if (error.response?.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorData = JSON.parse(errorText);
          message.error(errorData.message || errorData.error || t('dashboard.exportError'));
        } catch {
          message.error(error.message || t('dashboard.exportError'));
        }
      } else {
        message.error(error.message || t('dashboard.exportError'));
      }
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="reports-page">
      <div className="reports-header">
        <Title level={3}>{t('reports.tabs.sales', 'Sales report')}</Title>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Text strong>{t('salesRepresentatives.salesOverview.dateRange', 'Date range')}</Text>
          <RangePicker
            value={rangeDraft}
            onChange={(d) => setRangeDraft([d?.[0] || null, d?.[1] || null])}
          />
          <Button type="primary" onClick={() => setRangeApplied(rangeDraft)}>
            {t('salesRepresentatives.salesAnalytics.applyDateRange', 'Apply range')}
          </Button>
          {canDownloadReport && (
            <Button
              loading={exportLoading}
              onClick={handleExportReport}
              disabled={selectedRepresentatives.length === 0}
            >
              {t('salesRepresentatives.salesAnalytics.downloadResults', 'Download results')}
            </Button>
          )}
        </Space>
        {!canViewReport && canDownloadReport && (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary">{t('reports.downloadOnlyHint')}</Text>
          </div>
        )}
      </Card>

      {!canViewReport && canDownloadReport && (
        <Card title={t('reports.sales.title', 'Sales by representatives')} loading={salesLoading}>
          {reps.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-tertiary)' }}>
              {t('dashboard.noDataAvailable') || 'No data available'}
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 16 }}>
                <Checkbox
                  checked={selectedRepresentatives.length === reps.length}
                  indeterminate={
                    selectedRepresentatives.length > 0 &&
                    selectedRepresentatives.length < reps.length
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                >
                  <Text strong>
                    {t('common.selectAll') || 'Select All'} ({selectedRepresentatives.length}/
                    {reps.length})
                  </Text>
                </Checkbox>
              </div>
              <Space direction="vertical" style={{ width: '100%' }}>
                {reps.map((rep) => (
                  <Checkbox
                    key={rep.sales_representative_id}
                    checked={selectedRepresentatives.includes(rep.sales_representative_id)}
                    onChange={(e) =>
                      handleSelectRepresentative(rep.sales_representative_id, e.target.checked)
                    }
                  >
                    {rep.sales_representative_name}
                  </Checkbox>
                ))}
              </Space>
            </>
          )}
        </Card>
      )}

      {canViewReport && (
        <Card title={t('reports.sales.title', 'Sales by representatives')} loading={salesLoading}>
          {reps.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-tertiary)' }}>
              {t('dashboard.noDataAvailable') || 'No data available'}
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 16 }}>
                <Checkbox
                  checked={selectedRepresentatives.length === reps.length}
                  indeterminate={
                    selectedRepresentatives.length > 0 &&
                    selectedRepresentatives.length < reps.length
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                >
                  <Text strong>
                    {t('common.selectAll') || 'Select All'} ({selectedRepresentatives.length}/
                    {reps.length})
                  </Text>
                </Checkbox>
              </div>

              <Tabs
                activeKey={activeTab}
                onChange={(k) => setActiveTab(k)}
                type="card"
                items={reps.map((rep) => {
                  const repId = rep.sales_representative_id;
                  const details = representativeDetails[repId] || [];
                  const isLoadingDetails = loadingDetails[repId] || false;
                  let tabContent: React.ReactNode;
                  if (isLoadingDetails) {
                    tabContent = (
                      <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spin size="large" />
                      </div>
                    );
                  } else if (details.length === 0) {
                    tabContent = (
                      <div
                        style={{
                          textAlign: 'center',
                          padding: 40,
                          color: 'var(--color-text-tertiary)',
                        }}
                      >
                        {t('dashboard.noDataAvailable') || 'No data available'}
                      </div>
                    );
                  } else {
                    tabContent = (
                      <Table
                        dataSource={details}
                        rowKey="customer_id"
                        pagination={false}
                        scroll={{ y: 400 }}
                        columns={[
                          {
                            title: t('dashboard.customer') || 'Customer',
                            dataIndex: 'customer_name',
                            key: 'customer_name',
                            render: (text: string) => <Text strong>{text}</Text>,
                          },
                          {
                            title: t('dashboard.totalInvoicesAmount') || 'Total invoices amount',
                            dataIndex: 'total_invoices_amount',
                            key: 'total_invoices_amount',
                            align: 'right' as const,
                            render: (value: number) => <Text>{renderAmount(value)}</Text>,
                          },
                          {
                            title: t('dashboard.totalPaidAmount') || 'Total paid amount',
                            dataIndex: 'total_paid_amount',
                            key: 'total_paid_amount',
                            align: 'right' as const,
                            render: (value: number) => <Text>{renderAmount(value)}</Text>,
                          },
                          {
                            title: t('dashboard.totalDueAmount') || 'Total due amount',
                            dataIndex: 'total_due_amount',
                            key: 'total_due_amount',
                            align: 'right' as const,
                            render: (value: number) => <Text>{renderAmount(value)}</Text>,
                          },
                          {
                            title: t('dashboard.paymentPercentage') || 'Payment %',
                            dataIndex: 'payment_percentage',
                            key: 'payment_percentage',
                            align: 'right' as const,
                            render: (_value: number | null, record: RepDetailRow) => (
                              <SalesRepPaymentPercentage row={record} />
                            ),
                          },
                          {
                            title: t('dashboard.totalReceiptsAmount') || 'Total receipts amount',
                            dataIndex: 'total_receipts_amount',
                            key: 'total_receipts_amount',
                            align: 'right' as const,
                            render: (value: number) => <Text>{renderAmount(value)}</Text>,
                          },
                        ]}
                        summary={(pageData) => {
                          const rows = pageData as RepDetailRow[];
                          const customersWithInvoices = rows.filter(hasInvoicesInPeriod);
                          const totalPct = totalPaymentPercentage(rows);
                          const rowTotals = rows.reduce(
                            (
                              acc: {
                                total_invoices_amount: number;
                                total_paid_amount: number;
                                total_due_amount: number;
                                total_receipts_amount: number;
                              },
                              item: RepDetailRow
                            ) => {
                              acc.total_invoices_amount +=
                                Number.parseFloat(String(item.total_invoices_amount)) || 0;
                              acc.total_paid_amount +=
                                Number.parseFloat(String(item.total_paid_amount)) || 0;
                              acc.total_due_amount +=
                                Number.parseFloat(String(item.total_due_amount)) || 0;
                              acc.total_receipts_amount +=
                                Number.parseFloat(String(item.total_receipts_amount)) || 0;
                              return acc;
                            },
                            {
                              total_invoices_amount: 0,
                              total_paid_amount: 0,
                              total_due_amount: 0,
                              total_receipts_amount: 0,
                            }
                          );

                          return (
                            <Table.Summary fixed>
                              <Table.Summary.Row
                                style={{
                                  backgroundColor: '#fafafa',
                                  fontWeight: 'bold',
                                }}
                              >
                                <Table.Summary.Cell index={0} align="left">
                                  <Text strong>
                                    {t('dashboard.total') || 'الإجمالي'} (
                                    {customersWithInvoices.length}/{rows.length})
                                  </Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right">
                                  <Text strong>
                                    {renderAmount(rowTotals.total_invoices_amount)}
                                  </Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2} align="right">
                                  <Text strong>{renderAmount(rowTotals.total_paid_amount)}</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={3} align="right">
                                  <Text strong>{renderAmount(rowTotals.total_due_amount)}</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={4} align="right">
                                  <Text strong>{totalPct.toFixed(2)}%</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={5} align="right">
                                  <Text strong>
                                    {renderAmount(rowTotals.total_receipts_amount)}
                                  </Text>
                                </Table.Summary.Cell>
                              </Table.Summary.Row>
                            </Table.Summary>
                          );
                        }}
                      />
                    );
                  }

                  return {
                    key: String(repId),
                    label: (
                      <span>
                        <Checkbox
                          checked={selectedRepresentatives.includes(repId)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectRepresentative(repId, e.target.checked);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />{' '}
                        {rep.sales_representative_name}
                      </span>
                    ),
                    children: tabContent,
                  };
                })}
              />
            </>
          )}
        </Card>
      )}
    </div>
  );
};

export default SalesReport;
